import * as React from "react";
import * as moment from "moment";
import * as fileUtil from "../../common/Methods/FileUtil";

import { observer, inject } from "mobx-react";
import BootstrapTable from "react-bootstrap-table-next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faDownload, faTrashAlt, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { UncontrolledTooltip } from "reactstrap";

import { HorseForSaleStore } from "src/stores/HorseForSaleStore";
import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { AttachmentLogEndpoints } from "src/endpoints/AttachmentLogEndpoints";
import { FileUploadRequest } from "src/requests/CommonRequest";
import { AttachmentFile } from "src/model/Attachment";

import LoadingWidget from "../common/LoadingWidget";
import AttachmentsForm from "../common/AttachmentsForm";
import { errorMessage, successMessage } from "../common/Toast";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { SelectGroup } from "../common/SimpleFormComponents";
import { ConfigurationEndpoints } from "src/endpoints/ConfigurationEndpoints";

export interface Props {
    horseForSaleStore?: HorseForSaleStore;
    configurationStore?: ConfigurationStore;
    configurationEndpoints?: ConfigurationEndpoints;
    attachmentLogEndpoints?: AttachmentLogEndpoints;
}

export interface State {
    selectedSaleId: number | null;
    attachmentLogFiles: Array<FileUploadRequest>;
    attachmentLogId: number | null;
    wasValidated: boolean;
    loading: boolean;
    photoList: Array<AttachmentFile>;
}

const DefaultState = (): State => {
    return {
        attachmentLogId: null,
        selectedSaleId: null,
        attachmentLogFiles: [],
        wasValidated: false,
        loading: false,
        photoList: []
    };
};

@inject("configurationEndpoints")
@inject("attachmentLogEndpoints")
@inject("horseForSaleStore")
@inject("configurationStore")
@observer
export class WinningBidders extends React.Component<Props, State> {
    state: State = DefaultState();

    constructor(props: Props) {
        super(props);
    }

    getAttachment = async (file: AttachmentFile) => {
        try {
            const blob = await this.props.attachmentLogEndpoints!.getAttachment(file.id);
            fileUtil.DownloadBlob(file.name, blob);
        } catch (error) {
            errorMessage(error);
        }
    };

    deleteAttachment = async (file: AttachmentFile) => {
        const goAhead = await SimpleConfirmation(`Are you sure you'd like to delete '${file.name}'?`);
        if (!goAhead) {
            return;
        }

        try {
            await this.props.attachmentLogEndpoints!.deleteAttachment(file.id);
            successMessage(`${file.name} has been deleted.`);
            if (this.state.selectedSaleId) {
                this.getSaleBidderPhotos(this.state.selectedSaleId);
            }
        } catch (error) {
            errorMessage(error);
        }
    };

    uploadAttachment = async () => {
        try {
            if (this.state.attachmentLogId) {
                await this.props.attachmentLogEndpoints!.addAttachment(this.state.attachmentLogId, this.state.attachmentLogFiles);
            }

            successMessage(`File(s) successfully uploaded.`);
            this.setState({ attachmentLogFiles: [] });
            if (this.state.selectedSaleId) {
                this.getSaleBidderPhotos(this.state.selectedSaleId);
            }
        } catch (error) {
            errorMessage(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    getSaleBidderPhotos = async (saleId: number) => {
        try {
            const attachmentLogPhotos = await this.props.configurationEndpoints!.getSaleBidderPhotos(saleId);
            // if (attachmentLogPhotos.files.length > 0) {
            // console.log(attachmentLogPhotos);
            this.setState({ photoList: attachmentLogPhotos.files, attachmentLogId: attachmentLogPhotos.id });
            // }
        } catch (error) {
            errorMessage(error);
        }
    };

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div>No Configuration Store found.</div>;
        }

        const columns = [
            {
                dataField: "sale.name",
                text: "Sale",
                formatter: (cellContent: any, row: any) => {
                    if (this.state.selectedSaleId) {
                        const sale = configurationStore.horseSales.find(s => s.id === this.state.selectedSaleId!);
                        return sale ? sale.name : "";
                    } else {
                        return "";
                    }
                }
            },
            {
                dataField: "name",
                text: "File Name"
            },
            {
                dataField: "dateUploaded",
                text: "Date Uploaded",
                formatter: (cellContent: any, row: any) => moment(row.dateUploaded).format("YYYY-MM-DD")
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "100px", textAlign: "center" },
                formatter: (cellContent: any, row: AttachmentFile) => {
                    return (
                        <>
                            <button
                                type="text"
                                className="btn btn-secondary btn-sm mr-2"
                                id={`tooltipDownload${row.id}`}
                                title="Download File"
                                onClick={() => this.getAttachment(row)}
                            >
                                <FontAwesomeIcon icon={faDownload} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDownload${row.id}`} delay={0} className="tooltipstrap">
                                Download File
                            </UncontrolledTooltip>

                            <button
                                type="text"
                                className="btn btn-secondary btn-sm"
                                id={`tooltipDelete${row.id}`}
                                title="Delete File"
                                onClick={() => this.deleteAttachment(row)}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDelete${row.id}`} delay={0} className="tooltipstrap">
                                Delete File
                            </UncontrolledTooltip>
                        </>
                    );
                }
            }
        ];

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Winning Bidders Pictures
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-12">
                            <h1>Winning Bidders Pictures</h1>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12">
                            <div className="card shadow">
                                <div className="card-body">
                                    <div className="form-row">
                                        <SelectGroup
                                            label="Sale:"
                                            name="sale"
                                            isRequired={false}
                                            className={`col-md-12`}
                                            disabled={false}
                                            value={this.state.selectedSaleId || ""}
                                            onChange={e => {
                                                e.preventDefault();
                                                const selectedSaleId = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                                this.setState({ selectedSaleId });
                                                if (selectedSaleId) {
                                                    this.getSaleBidderPhotos(selectedSaleId);
                                                }
                                            }}
                                        >
                                            <option value="">Select sale...</option>
                                            {configurationStore!.horseSales
                                                .slice()
                                                .sort((a: any, b: any) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                                .map((c: any, i: any) => (
                                                    <option key={i} value={c.id}>
                                                        {c.name}
                                                    </option>
                                                ))}
                                        </SelectGroup>
                                    </div>
                                    {this.state.selectedSaleId && (
                                        <div className="card">
                                            <div className="card-body bg-light">
                                                {this.state.loading && (
                                                    <div className="form-row">
                                                        <div className="col-12">
                                                            <LoadingWidget />
                                                        </div>
                                                    </div>
                                                )}

                                                {!this.state.loading && (
                                                    <div className="form-row">
                                                        <div className="col-12">
                                                            <div className="alert alert-warning text-center">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
                                                                Maximum upload size limit of 100MB.
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="form-row">
                                                    <div className={`form-group col-12 col-md-10`}>
                                                        <label htmlFor="files">Upload a single file or multiple files at once:</label>
                                                        <AttachmentsForm
                                                            attachments={this.state.attachmentLogFiles}
                                                            updateAttachmentLog={(attachmentLogFiles: Array<FileUploadRequest>) =>
                                                                this.setState({ attachmentLogFiles })
                                                            }
                                                            disabled={!this.state.selectedSaleId}
                                                            multiple={true}
                                                        />
                                                    </div>
                                                    <div className="col-12 col-md-2 mt-4">
                                                        <button
                                                            type="submit"
                                                            className="btn btn-primary mt-2 pb-2 btn-block"
                                                            disabled={this.state.attachmentLogFiles.length === 0 || this.state.loading}
                                                            onClick={() => {
                                                                this.setState({ loading: true });
                                                                this.uploadAttachment();
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faUpload} className="mr-2" /> Upload File(s)
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="form-row mt-5">
                                        <div className="col-12">
                                            <div className="table-responsive">
                                                {
                                                    <BootstrapTable
                                                        keyField="id"
                                                        remote={true}
                                                        hover={true}
                                                        bordered={false}
                                                        columns={columns}
                                                        bootstrap4={true}
                                                        data={this.state.photoList}
                                                        noDataIndication={() => (
                                                            <div>
                                                                <span>No results found.</span>
                                                            </div>
                                                        )}
                                                    />
                                                }
                                                {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                                        Otherwise the `noDataIndication` wouldn't work */}
                                                {/* {horseForSaleListStore.loading && <div />} */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/WinningBiddersPictures.tsx